"use client";

import type { StickyContentListBlockV2 } from "@mw/shared/payload-types";

import { Button, ButtonProps } from "@ui/components/core/Button";
import { Image } from "@ui/components/core/Image";
import { ModalVideoPlayer } from "@ui/components/core/ModalVideoPlayer";
import { SimpleVideoPlayer } from "@ui/components/core/SimpleSquareVideo";
import Play20Icon from "@ui/icons/20x20/play-20.svg";
import { ImageSizes } from "@ui/lib/image";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { getLinkProps, getVideoUrl } from "@utilities/payload";
import { useScroll } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { Block } from "../Block";
import { cva, VariantProps } from "class-variance-authority";
import { MakeNullable, MultiTemplateField } from "@utilities/types";
import { assertNever } from "@mw/shared/asserts";
import { ILink, Link } from "@ui/components/core/Link";
import Quote from "@ui/components/core/Quote";
import clsx from "clsx";
import { ComponentRenderer, type StickyContentComponentName } from "./components";
import { StickyContentProvider } from "./StickyContentContext";

type StickyContentItemFooterItem = MultiTemplateField<
  NonNullable<
    Required<NonNullable<StickyContentListBlockV2["items"]>>[number]["stickyContent"]["footerContent"]
  >[number]["content"]
>;

const footerItemContainerVariants = cva("pt-12 lg:pt-48", {
  variants: {
    contained: {
      true: "px-12 lg:px-48",
      false: "",
    },
    last: {
      true: "pb-12 lg:pb-48",
      false: "",
    },
  },
  defaultVariants: {
    contained: false,
    last: false,
  },
});

const FloaterContentFooter = ({ items }: { items: StickyContentItemFooterItem[] | null }) => {
  const t = useTranslations("ClientKeys.Globals");
  if (!items || items.length === 0) return null;

  return (
    <>
      {items?.map((item, index) => {
        switch (item.template) {
          case "labels":
            return (
              <div
                className={footerItemContainerVariants({
                  contained: true,
                  last: index === items.length - 1,
                  className: "space-y-16",
                })}
                key={index}
              >
                {item.labels?.map((label, labelIndex) => (
                  <div className='f-paragraph-02 flex items-center justify-between' key={labelIndex}>
                    <span className='text-light'>{label.title}</span>
                    <span className=''>{label.value}</span>
                  </div>
                ))}
              </div>
            );
          case "openVideoButton":
            const videoUrl = getVideoUrl(item.openVideoButton?.video);
            if (!videoUrl) return null;
            return (
              <ModalVideoPlayer
                key={index}
                customTrigger={
                  <Button
                    className='w-full rounded-lg'
                    icon={<Play20Icon />}
                    iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
                    size='lg'
                    variant='elevated'
                  >
                    {t("watch_full_video")}
                  </Button>
                }
                label={t("watch_full_video")}
                video={videoUrl}
              />
            );
          case "link":
            const link = getLinkProps(item.link);
            if (!link) return null;
            return (
              <div
                className={footerItemContainerVariants({ contained: true, last: index === items.length - 1 })}
                key={index}
              >
                <Link
                  {...link}
                  asButton
                  buttonVariants={{
                    variant: "elevated",
                    size: "lg",
                    expand: true,
                  }}
                  className='rounded-lg'
                />
              </div>
            );
          case "quote":
            if (!item.quote.text) return null;

            return (
              <div
                className={footerItemContainerVariants({ contained: true, last: index === items.length - 1 })}
                key={index}
              >
                <Quote quote={item.quote.text} title={item.quote.title} author={item.quote.author} className='' />
              </div>
            );
          default:
            throw assertNever("FloaterContentFooter", item);
        }
      })}
    </>
  );
};

type StickyContentItemFloater = NonNullable<StickyContentListBlockV2["items"]>[number]["stickyContent"];

interface FloaterContentProps {
  activeIndex?: number;
  autoPlayWhenInView?: boolean;
  background?: StickyContentListBlockV2["background"];
  content?: StickyContentItemFloater | null;
  onStickyRef?: (ref: HTMLImageElement | HTMLVideoElement | null) => void;
}

const FloaterContent = ({
  content,
  activeIndex,
  autoPlayWhenInView = false,
  background,
  onStickyRef,
}: FloaterContentProps) => {
  const imageRef = useRef<HTMLImageElement>(null);

  // Forward the image ref to parent
  useEffect(() => {
    if (onStickyRef && imageRef.current) {
      onStickyRef(imageRef.current);
    }
  }, [onStickyRef]);

  if (!content) return null;

  const imageLayout = content.layout || "center";
  const previewVideoUrl = content.template === "video" ? getVideoUrl(content.video?.previewVideo) : null;
  const imageResource = content.template === "image" ? content.image?.image : null;
  const backgroundResource = background && typeof background !== "string" ? background : null;

  return (
    <div className={clsx("relative", imageLayout === "stretch" ? "lg:px-0 lg:pt-0" : "lg:px-16 lg:pt-16")}>
      {backgroundResource && (
        <div className='absolute inset-0 overflow-hidden lg:hidden'>
          <Image
            className='h-full w-full object-cover'
            resource={backgroundResource}
            sizes={ImageSizes.FULL_HALF_ON_LG}
          />
          <div className='from-white absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t to-transparent' />
        </div>
      )}
      <m.div
        animate={{ filter: "blur(0px)", opacity: 1 }}
        className='relative'
        exit={{ filter: "blur(4px)", opacity: 0.5 }}
        initial={{ filter: "blur(4px)", opacity: 0.5 }}
        key={activeIndex}
        transition={{
          duration: 0.5,
          ease: [0.4, 0, 0.2, 1],
          filter: { duration: 0.5 },
        }}
      >
        {imageResource && content.template === "image" && (
          <div
            className={clsx(
              "relative aspect-1x1 w-full overflow-hidden",
              imageLayout === "center" && "rounded-xl lg:mt-16",
            )}
            ref={imageRef}
          >
            <Image
              className='h-full w-full object-cover'
              quality={100}
              resource={imageResource}
              sizes={ImageSizes.FULL_HALF_ON_LG}
            />
          </div>
        )}
        {previewVideoUrl && content.template === "video" && (
          <SimpleVideoPlayer
            autoPlayWhenInView={autoPlayWhenInView}
            className='aspect-1x1'
            onVideoRef={ref => {
              if (onStickyRef) {
                onStickyRef(ref);
              }
            }}
            src={previewVideoUrl}
          />
        )}
        {content.template === "component" && content.component?.template && (
          <div
            className={clsx(
              "relative aspect-1x1 w-full overflow-hidden",
              imageLayout === "center" && "rounded-xl lg:mt-16",
            )}
            ref={imageRef as React.RefObject<HTMLDivElement>}
          >
            <ComponentRenderer name={content.component.template as StickyContentComponentName} />
          </div>
        )}
        <FloaterContentFooter
          items={(content.footerContent?.map(fc => fc.content) as StickyContentItemFooterItem[]) || null}
        />
      </m.div>
    </div>
  );
};

const stickyContentItemVariants = cva("relative min-h-[500px] lg:px-48 lg:py-72", {
  variants: {
    borderStyle: {
      withBorder: "border-b border-r border-overlay-dark",
      noBorder: "flex flex-col justify-center items-center",
    },
    isLast: {
      true: "",
      false: "",
    },
    hasStretchedImage: {
      true: "px-0 pb-0",
      false: "px-16 py-32",
    },
  },
  defaultVariants: {
    borderStyle: "withBorder",
    isLast: false,
    hasStretchedImage: false,
  },
  compoundVariants: [
    {
      borderStyle: "withBorder",
      isLast: true,
      className: "border-b-0",
    },
  ],
});

type StickyContentItemVariants = VariantProps<typeof stickyContentItemVariants>;

export type StickyContentItem = Omit<NonNullable<StickyContentListBlockV2["items"]>[number], "description">;

interface StickyContentItemProps {
  index: number;
  isLast: boolean;
  item: StickyContentItem;
  background?: StickyContentListBlockV2["background"];
  borderStyle?: StickyContentItemVariants["borderStyle"];
}

const StickyContentItemFloater = ({ item, isLast, index, background, borderStyle }: StickyContentItemProps) => {
  const hasStretchedImage = item.stickyContent.layout === "stretch";

  return (
    <div className={stickyContentItemVariants({ borderStyle, isLast, hasStretchedImage })}>
      <div className='tw-gap-3 tw-pt-12 lg:tw-pt-0 tw-px-4 md:tw-px-12 lg:tw-px-0 flex flex-col'>
        <div className='tw-flex tw-items-center tw-gap-2'>
          {item.icon && <Image className='tw-h-7 tw-w-7' resource={item.icon} />}
          <h3 className='tw-type-3xl'>{item.title}</h3>
        </div>
        {item.description_html && <div className='tw-mt-3.5'>{item.description_html}</div>}
      </div>

      {/* Mobile floater content */}
      <div className='tw-mt-8 lg:tw-hidden'>
        <FloaterContent activeIndex={index} autoPlayWhenInView background={background} content={item.stickyContent} />
      </div>

      {/* Invisible element positioned to the right for intersection detection (desktop only) */}
      <div
        className='tw-pointer-events-none tw-absolute tw-left-full tw-top-0 tw-hidden tw-h-full tw-w-full lg:tw-block'
        data-intersection-trigger={index}
      />
      {isLast && <div className='tw-hidden tw-h-[400px] lg:tw-block' />}
    </div>
  );
};

interface StickyPanelProps {
  activeIndex: number;
  content: StickyContentItemFloater | null | undefined;
  onIntersection: (index: number) => void;
}

const StickyPanel = ({ content, activeIndex, onIntersection }: StickyPanelProps) => {
  const stickyRef = useRef<HTMLImageElement | HTMLVideoElement | null>(null);

  // Use motion's efficient scroll hook
  const { scrollY } = useScroll();

  const handleVideoRef = (ref: HTMLImageElement | HTMLVideoElement | null) => {
    stickyRef.current = ref;
  };

  useEffect(() => {
    if (!stickyRef.current) return;

    // Cache trigger elements once
    const triggerElements = document.querySelectorAll("[data-intersection-trigger]");

    const checkIntersection = () => {
      if (!stickyRef.current) return;

      const stickyRect = stickyRef.current.getBoundingClientRect();

      triggerElements.forEach(trigger => {
        const triggerRect = trigger.getBoundingClientRect();

        // Calculate vertical overlap percentage
        const overlapTop = Math.max(stickyRect.top, triggerRect.top);
        const overlapBottom = Math.min(stickyRect.bottom, triggerRect.bottom);
        const overlapHeight = Math.max(0, overlapBottom - overlapTop);

        const triggerHeight = triggerRect.height;
        const overlapPercentage = overlapHeight / triggerHeight;

        // Consider it an overlap if above 80% vertical overlap
        if (overlapPercentage >= 0.6 || (overlapPercentage > 0.3 && stickyRect.height < triggerHeight)) {
          const index = parseInt(trigger.getAttribute("data-intersection-trigger") || "0");
          onIntersection(index);
        }
      });
    };

    // Subscribe to scroll changes using motion's efficient system
    const unsubscribe = scrollY.on("change", checkIntersection);

    // Initial check
    const timeoutId = setTimeout(checkIntersection, 200);

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [onIntersection, scrollY]);

  if (!content) return null;

  return (
    <div className='hidden lg:block'>
      <FloaterContent activeIndex={activeIndex} content={content} onStickyRef={handleVideoRef} />
    </div>
  );
};

const stickyContentButtonVariants = cva("relative flex flex-col justify-center items-center p-16", {
  variants: {
    borderStyle: {
      withBorder: "border-r border-t border-overlay-dark",
      noBorder: "",
    },
  },
  defaultVariants: {
    borderStyle: "withBorder",
  },
});

export const StickyContentButton = ({
  borderStyle,
  variant,
  link,
}: {
  borderStyle: StickyContentItemVariants["borderStyle"];
  variant: ButtonProps["variant"];
  link: ILink;
}) => {
  return (
    <div className={stickyContentButtonVariants({ borderStyle })}>
      <Link
        {...link}
        asButton
        buttonVariants={{
          variant: variant === "secondary" ? "secondary" : "primary",
          size: "lg",
          expand: true,
        }}
        className='rounded-lg'
      />
    </div>
  );
};

type BlockStickyContentListClientProps = Omit<
  MakeNullable<StickyContentListBlockV2, "ctaButton">,
  "blockName" | "blockType" | "id" | "items"
> & {
  items: StickyContentItem[];
};

const BlockStickyContentListClient = ({
  items,
  borderStyle,
  ctaButton,
  background,
}: BlockStickyContentListClientProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const stickyPanelRef = useRef<HTMLDivElement>(null);

  const handleIntersection = (index: number) => {
    setActiveIndex(index);
  };

  const activeContent = items?.[activeIndex]?.stickyContent;

  const ctaLink = getLinkProps(ctaButton);

  const backgroundResource = background && typeof background !== "string" ? background : null;

  return (
    <Block className='container' name='sticky-content'>
      <StickyContentProvider activeIndex={activeIndex} items={items}>
        <div className='relative grid grid-cols-1 lg:grid-cols-2'>
          {/* Left column - Content list */}
          <div className='relative'>
            {items?.map((item, index) => (
              <div className='relative' key={index}>
                <StickyContentItemFloater
                  index={index}
                  isLast={index === items.length - 1}
                  item={item}
                  background={background}
                  borderStyle={borderStyle}
                />
              </div>
            ))}

            {ctaLink && (
              <div className='relative'>
                <StickyContentButton
                  borderStyle={borderStyle}
                  variant={ctaButton!.appearance || "secondary"}
                  link={ctaLink}
                />
              </div>
            )}
          </div>

          {/* Right column - Background + Sticky panel */}
          <div className='relative'>
            {backgroundResource && (
              <div className='absolute inset-0 overflow-hidden'>
                <Image
                  className='h-full w-full object-cover'
                  resource={backgroundResource}
                  sizes={ImageSizes.FULL_HALF_ON_LG}
                />
                <div className='from-white absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t to-transparent' />
              </div>
            )}
            <div
              className='lg:sticky lg:self-start'
              ref={stickyPanelRef}
              style={{
                top: "calc(16px + var(--contextual-nav-height))",
              }}
            >
              <StickyPanel activeIndex={activeIndex} content={activeContent} onIntersection={handleIntersection} />
            </div>
          </div>
        </div>
      </StickyContentProvider>
    </Block>
  );
};

export { BlockStickyContentListClient };
